import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import SEO from "../components/SEO";
import publicAuctionsImage from "../assets/img/services/public-auctions.jpg";
import debtCollectionsImage from "../assets/img/services/debt-collections.jpg";
import courtProcessServiceImage from "../assets/img/services/court-process-service.jpg";
import repossessionsImage from "../assets/img/services/repossessions-of-chattels-mortgage.jpg";
import distressOfRentImage from "../assets/img/services/distress-of-rent.jpg";
import realisationImage from "../assets/img/services/realisation-of-charged-securities.jpg";
import privateInvestigationsImage from "../assets/img/services/private-investigations.jpg";
import courtWarrantsImage from "../assets/img/services/court-warrants-executions.jpg";
import otherServicesImage from "../assets/img/services/other-services.jpg";
import {
    FaGavel,
    FaBuilding,
    FaBalanceScale,
    FaBriefcase,
    FaHandshake,
    FaSearch,
} from "react-icons/fa";
import { MdVerified } from "react-icons/md";

const SERVICES = {
    "public-auctions": {
        title: "Public Auctions",
        image: publicAuctionsImage,
        description: [
            "We hold Public Auctions throughout the Republic of Kenya. We offer services to companies upgrading and modernizing their equipment, financial institutions disposing of securities, and Courts disposing of exhibits and forfeited property.",
            "Government corporations disposing of old assets, storage facility companies as well as individuals in need of disposing of old assets including household goods. The commission rates are agreed up-front between the client and ourselves.",
        ],
    },

    "debt-collections": {
        title: "Debt Collections",
        image: debtCollectionsImage,
        description: [
            "We offer effective and efficient debt collection services to our clients.",
            "We have a team of experienced negotiators who will handle all aspects of debt collection to the satisfaction of our clients.",
            "We have successfully collected debts for both local and international companies as well as individuals.",
            "We charge commissions out of the monies we successfully recover for clients from the debtor.",
        ],
    },

    "court-process-service": {
        title: "Court Process Service",
        image: courtProcessServiceImage,
        description: [
            "We execute the process at the shortest time possible bearing in mind that 'justice delayed is justice denied'.",
            "Our team of certified court process servers will handle all your assignments with the utmost professionalism.",
        ],
    },

    "repossessions-of-chattels-mortgage": {
        title: "Repossessions of Chattels and Mortgage",
        image: repossessionsImage,
        description: [
            "We recover possession of property acquired through hire purchase on behalf of the owners upon being instructed so to do.",
        ],
    },

    "distress-of-rent": {
        title: "Distress of Rent Service",
        image: distressOfRentImage,
        description: [
            "We levy distress for rent in various cities in Kenya. In doing this, we follow the provisions of the Distress for Rent Act Chapter 293, Laws of Kenya, which regulates distress for rent activities.",
            "We offer this service to Real Estate Managers and Agents, Advocates acting on behalf of their clients, Property owners and landlords.",
        ],
    },

    "realisation-of-charged-securities": {
        title: "Realisation of Charged Securities / Property Auctions",
        image: realisationImage,
        description: [
            "We realize immovable properties from every corner of the country.",
            "We have over the years realized charged securities in form of land and buildings, office furniture and equipment, Motor Vehicles, and Household assets amongst other securities.",
        ],
    },

    "private-investigations": {
        title: "Private Investigations",
        image: privateInvestigationsImage,
        description: [
            "We conduct private investigation for individual and corporate clients. This is done taking into consideration the Data Protection Act.",
            "We provide service to organizations such as Lawyers, Banks, and Corporate bodies as well as to individual clients.",
        ],
    },

    "court-warrants-executions": {
        title: "Court Warrants Executions",
        image: courtWarrantsImage,
        description: [
            "We seize assets pursuant to Court warrants of attachment issued by the commercial courts following litigations in civil matters.",
        ],
    },

    "other-services": {
        title: "Other Services",
        image: otherServicesImage,
        description: [
            "We are also capable of offering the following services:",
            "Tracing and ascertaining the assets belonging to the runaway debtor.",
            "Assisting Companies, individuals and Law firms that wish to execute warrants of attachment and warrants of arrest against judgment debtors who are not easily traceable.",
            "Assisting litigants wishing to serve court processes upon parties whose physical addresses are not known.",
            "Assisting employers on staff investigations on their after-duty involvements and behavior.",
            "Conducting background check and background investigation.",
            "We also undertake the above services in the East Africa Countries of Uganda, Tanzania, Rwanda and Burundi in partnership with Licensed Auctioneers in the named countries.",
        ],
    },
};

const OVERVIEW_ITEMS = [
    { icon: FaGavel, label: "Public Auctions", slug: "public-auctions" },
    { icon: FaBuilding, label: "Distress for Rent", slug: "distress-of-rent" },
    { icon: MdVerified, label: "Realisation of Charged Securities", slug: "realisation-of-charged-securities" },
    { icon: FaBalanceScale, label: "Execution of Court Orders", slug: "court-warrants-executions" },
    { icon: FaBuilding, label: "Court Eviction Orders", slug: "court-warrants-executions" },
    { icon: FaGavel, label: "Repossessions of Chattels & Mortgage", slug: "repossessions-of-chattels-mortgage" },
    { icon: FaBriefcase, label: "Court Process Serving", slug: "court-process-service" },
    { icon: FaHandshake, label: "Debt Collection", slug: "debt-collections" },
    { icon: FaSearch, label: "Private Investigations", slug: "private-investigations" },
];

export default function PublicService() {
    const { serviceSlug } = useParams();
    const navigate = useNavigate();

    if (!serviceSlug) {
        return (
            <>
                <SEO
                    title="Our Services"
                    description="Sare Auctioneers provides a range of auctioneering and related professional services."
                />
                <TopNavbar />

                <Page>
                    <div className="container">
                        <p className="eyebrow">Sare Auctioneers Services</p>
                        <h1>Our Areas of Service</h1>

                        <Content style={{ maxWidth: 700 }}>
                            <p>Sare Auctioneers provides a range of auctioneering and related professional services.</p>
                        </Content>

                        <ServicesGrid>
                            {OVERVIEW_ITEMS.map(({ icon: Icon, label, slug }) => (
                                <ServiceItem key={label} onClick={() => navigate(`/services/${slug}`)}>
                                    <ServiceIcon>
                                        <Icon size={22} />
                                    </ServiceIcon>
                                    <span>{label}</span>
                                </ServiceItem>
                            ))}
                        </ServicesGrid>

                        <CapabilityText>
                            <strong>
                                Auctioneers, Debt Collection, Official Brokers, Repossessors,
                                Process Servers, Private Investigators and Bailiffs Commission Agents
                            </strong>
                        </CapabilityText>
                    </div>
                </Page>

                <Footer />
            </>
        );
    }

    const service = SERVICES[serviceSlug];

    if (!service) {
        return (
            <>
                <SEO
                    title="Services"
                    description="Professional auctioneering and related services from Sare Auctioneers."
                />

                <TopNavbar />

                <Page>
                    <div className="container">
                        <p className="eyebrow">Sare Auctioneers Services</p>
                        <h1>Service Not Found</h1>

                        <Content>
                            <p>
                                The service you are looking for could not be found.
                            </p>
                        </Content>
                    </div>
                </Page>

                <Footer />
            </>
        );
    }

    return (
        <>
            <SEO
                title={`${service.title} | Sare Auctioneers`}
                description={service.description}
            />

            <TopNavbar />

            <Page>
                <div className="container">
                    <p className="eyebrow">Sare Auctioneers Services</p>

                    <h1>{service.title}</h1>

                    <ServiceLayout>
                        <ServiceImage
                            src={service.image}
                            alt={service.title}
                        />

                       <Content>
    {service.description.map((para, i) => (
        <p key={i}>{para}</p>
    ))}
</Content>
                    </ServiceLayout>
                </div>
            </Page>

            <Footer />
        </>
    );
}

const Page = styled.main`
  min-height: 70vh;
  padding: 150px 0 90px;
  color: #3b1718;

  .eyebrow {
    color: #8a3638;
    font-weight: 700;
    margin-bottom: 10px;
  }

  h1 {
    margin: 0 0 20px;
    font-size: 42px;
  }
`;

const ServiceImage = styled.img`
  width: 45%;
  min-width: 350px;
  height: 420px;
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    height: 280px;
  }
`;

const Content = styled.div`
  flex: 1;
  color: #5d191a;
  line-height: 1.8;

  p {
    margin: 0 0 20px;
    font-size: 18px;
  }
`;

const ServiceLayout = styled.div`
  display: flex;
  gap: 50px;
  align-items: flex-start;
  margin-top: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 40px 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid #eadfe0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  font-weight: 600;
  color: #3b1718;

  &:hover {
    background: #f8eeee;
  }
`;

const ServiceIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f8eeee;
  color: #5d191a;
  flex-shrink: 0;
`;

const CapabilityText = styled.p`
  margin-top: 20px;
  font-size: 15px;
  color: #5d191a;
`;